/**
 * AdjustmentCard - Displays a single adjustment with edit/delete actions
 */

import React from 'react';
import { Pencil, Trash2, Clock, AlertTriangle, DollarSign, Percent } from 'lucide-react';
import { formatAdjustmentValue, formatMonthRange } from '../../hooks/useAdjustments';
import type { ForecastAdjustment } from '../../api';

interface AdjustmentCardProps {
  adjustment: ForecastAdjustment;
  isExpired?: boolean;
  isExpiringSoon?: boolean;
  currency?: string | null;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
  isDeleting?: boolean;
}

export function AdjustmentCard({
  adjustment,
  isExpired = false,
  isExpiringSoon = false,
  currency,
  onEdit,
  onDelete,
  isReadOnly = false,
  isDeleting = false,
}: AdjustmentCardProps) {
  const isFlat = adjustment.adjustmentType === 'FLAT';
  const isPositive = adjustment.value >= 0;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        isExpired
          ? 'bg-slate-50 border-slate-200'
          : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Type Badge */}
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
              isFlat
                ? 'bg-blue-100 text-blue-700'
                : 'bg-purple-100 text-purple-700'
            }`}
          >
            {isFlat ? <DollarSign size={10} /> : <Percent size={10} />}
            {isFlat ? 'Flat' : 'Percent'}
          </span>

          {/* Expiring Soon Warning */}
          {isExpiringSoon && !isExpired && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
              <AlertTriangle size={10} />
              Expiring soon
            </span>
          )}

          {/* Expired Badge */}
          {isExpired && (
            <span className="text-xs text-slate-400">Expired</span>
          )}
        </div>

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              title="Edit adjustment"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title="Delete adjustment"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Value */}
      <div
        className={`text-lg font-semibold mb-1 ${
          isExpired
            ? 'text-slate-400'
            : isPositive
            ? 'text-emerald-600'
            : 'text-red-600'
        }`}
      >
        {isPositive && !isFlat ? '+' : ''}
        {formatAdjustmentValue(adjustment.adjustmentType, adjustment.value, currency)}
      </div>

      {/* Description */}
      {adjustment.description && (
        <p className={`text-sm mb-2 ${isExpired ? 'text-slate-400' : 'text-slate-600'}`}>
          {adjustment.description}
        </p>
      )}

      {/* Meta Row */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
        {/* Month Range */}
        <span className="inline-flex items-center gap-1">
          <Clock size={12} />
          {formatMonthRange(adjustment.startMonth, adjustment.endMonth)}
        </span>

        {/* Expiration */}
        {adjustment.expiresAt && (
          <span
            className={`inline-flex items-center gap-1 ${
              isExpired
                ? 'text-slate-400'
                : isExpiringSoon
                ? 'text-amber-600 font-medium'
                : 'text-slate-500'
            }`}
          >
            {isExpired ? 'Expired' : 'Expires'} {formatDate(adjustment.expiresAt)}
          </span>
        )}
      </div>
    </div>
  );
}

export default AdjustmentCard;
